import { useEffect, useRef, useCallback, useState } from 'react';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface UseBackgroundMusicReturn {
  start: () => void;
  stop: () => void;
  setIntensity: (intensity: MusicIntensity) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  isMuted: boolean;
  volume: number;
  isPlaying: boolean;
}

// C Major scale frequencies (A4 = 440Hz tuning)
const NOTE_FREQUENCIES: Record<string, number> = {
  'C3': 130.81, 'D3': 146.83, 'E3': 164.81, 'F3': 174.61, 'G3': 196.00, 'A3': 220.00, 'B3': 246.94,
  'C4': 261.63, 'D4': 293.66, 'E4': 329.63, 'F4': 349.23, 'G4': 392.00, 'A4': 440.00, 'B4': 493.88,
  'C5': 523.25, 'D5': 587.33, 'E5': 659.25, 'F5': 698.46, 'G5': 783.99, 'A5': 880.00, 'B5': 987.77,
  'C6': 1046.50, 'D6': 1174.66, 'E6': 1318.51, 'G6': 1567.98,
};

// Catchy bouncy melodies with wide leaps (C major, staccato)
const MELODY_PATTERNS: Record<MusicIntensity, string[][]> = {
  calm: [
    ['C5', 'G5', 'E5', 'C6', 'G5', 'E5', 'D5', 'G5', 'C5', 'E5', 'G5', 'C6', 'B5', 'G5', 'E5', 'D5'],
    ['E5', 'C6', 'G5', 'E6', 'C6', 'G5', 'E5', 'G5', 'C5', 'G5', 'E5', 'C6', 'G5', 'E5', 'D5', 'C5'],
    ['G5', 'E5', 'C6', 'G5', 'E6', 'C6', 'G5', 'E5', 'D5', 'G5', 'C6', 'E6', 'C6', 'G5', 'E5', 'C5'],
  ],
  waiting: [
    ['C5', 'E5', 'G5', 'C6', 'E6', 'C6', 'G5', 'E5', 'D5', 'G5', 'B5', 'D6', 'B5', 'G5', 'D5', 'G5'],
    ['G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'E5', 'C5', 'E5', 'G5', 'C6', 'G5', 'E5', 'C5', 'G4'],
  ],
  medium: [
    ['C5', 'E5', 'G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'C6', 'E6', 'C6', 'G5', 'E5', 'D5', 'E5'],
    ['G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'E5', 'C5', 'E5', 'G5', 'E5', 'C5', 'G4', 'C5', 'E5'],
    ['E5', 'G5', 'C6', 'E6', 'G6', 'E6', 'D6', 'B5', 'G5', 'B5', 'D6', 'B5', 'G5', 'D5', 'G5', 'B5'],
  ],
  intense: [
    ['C5', 'G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'E5', 'G5', 'C6', 'E6', 'D6', 'B5', 'G5', 'D5'],
    ['E5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'C6', 'E6', 'G6', 'E6', 'D6', 'B5', 'G5', 'E5', 'G5'],
    ['G5', 'E6', 'C6', 'G6', 'E6', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'C6', 'E6', 'D6', 'B5', 'G5'],
  ],
  critical: [
    ['C5', 'E6', 'G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'C6', 'E6', 'G6', 'E6', 'D6', 'B5', 'G5'],
    ['E5', 'G6', 'E6', 'C6', 'E6', 'G6', 'E6', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'E5', 'G5', 'C6'],
  ],
  victory: [
    ['C5', 'E5', 'G5', 'C6', 'E6', 'G6', 'E6', 'C6', 'G5', 'C6', 'E6', 'G6', 'C6', 'E6', 'G6', 'C6'],
  ],
  defeat: [
    ['E5', 'D5', 'C5', 'B4', 'A4', 'G4', 'F4', 'E4', 'D4', 'C4', 'B3', 'A3', 'G3', 'F3', 'E3', 'C3'],
  ],
};

// Pizzicato string patterns (chord progressions)
const PIZZ_PATTERNS: Record<MusicIntensity, string[][][]> = {
  calm: [[['C4', 'E4', 'G4', 'C5'], ['G3', 'B3', 'D4', 'G4'], ['A3', 'C4', 'E4', 'A4'], ['F3', 'A3', 'C4', 'F4']]],
  waiting: [[['C4', 'G4', 'C5', 'E5'], ['G3', 'D4', 'G4', 'B4'], ['F3', 'C4', 'F4', 'A4'], ['C4', 'G4', 'C5', 'E5']]],
  medium: [[['C4', 'E4', 'G4', 'C5'], ['F3', 'A3', 'C4', 'F4'], ['G3', 'B3', 'D4', 'G4'], ['C4', 'E4', 'G4', 'C5']]],
  intense: [[['C4', 'G4', 'C5', 'E5'], ['F3', 'C4', 'F4', 'A4'], ['G3', 'D4', 'G4', 'B4'], ['C4', 'G4', 'C5', 'E5']]],
  critical: [[['C4', 'E4', 'G4', 'C5'], ['C4', 'E4', 'G4', 'C5'], ['G3', 'B3', 'D4', 'G4'], ['C4', 'E4', 'G4', 'C5']]],
  victory: [[['C4', 'E4', 'G4', 'C5', 'E5', 'G5', 'C6', 'E6']]],
  defeat: [[['A3', 'C4', 'E4'], ['F3', 'A3', 'C4'], ['D3', 'F3', 'A3'], ['E3', 'G3', 'B3']]],
};

// Bass patterns
const BASS_PATTERNS: Record<MusicIntensity, string[]> = {
  calm: ['C3', 'C3', 'G3', 'G3', 'A3', 'A3', 'F3', 'G3'],
  waiting: ['C3', 'C3', 'G3', 'G3', 'F3', 'F3', 'G3', 'G3'],
  medium: ['C3', 'C3', 'F3', 'F3', 'G3', 'G3', 'C3', 'G3'],
  intense: ['C3', 'C3', 'C3', 'F3', 'F3', 'G3', 'G3', 'C3'],
  critical: ['C3', 'C3', 'C3', 'C3', 'G3', 'G3', 'C3', 'C3'],
  victory: ['C3', 'E3', 'G3', 'C4', 'G3', 'E3', 'C3', 'G3'],
  defeat: ['A3', 'G3', 'F3', 'E3', 'D3', 'C3', 'B3', 'A3'],
};

// Tempo per intensity
const TEMPO_MAP: Record<MusicIntensity, number> = {
  calm: 130,
  waiting: 140,
  medium: 150,
  intense: 160,
  critical: 170,
  victory: 150,
  defeat: 90,
};

export const useBackgroundMusic = (): UseBackgroundMusicReturn => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const masterGainRef = useRef<GainNode | null>(null);
  const compressorRef = useRef<DynamicsCompressorNode | null>(null);
  const reverbRef = useRef<ConvolverNode | null>(null);
  const isPlayingRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const intensityRef = useRef<MusicIntensity>('calm');
  const patternIndexRef = useRef(0);
  const loopCountRef = useRef(0);
  
  const [isMuted, setIsMuted] = useState(() => {
    const saved = localStorage.getItem('67chaos-music-muted');
    return saved === 'true';
  });
  
  const [volume, setVolumeState] = useState(() => {
    const saved = localStorage.getItem('67chaos-music-volume');
    return saved ? parseFloat(saved) : 0.35;
  });
  
  const [isPlaying, setIsPlaying] = useState(false);

  // Create reverb impulse response
  const createReverb = useCallback((ctx: AudioContext): ConvolverNode => {
    const convolver = ctx.createConvolver();
    const rate = ctx.sampleRate;
    const length = rate * 1.5;
    const impulse = ctx.createBuffer(2, length, rate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
      }
    }
    
    convolver.buffer = impulse;
    return convolver;
  }, []);

  // Glockenspiel/marimba (bright, bell-like)
  const playGlockenspiel = useCallback((
    ctx: AudioContext, 
    freq: number, 
    time: number, 
    duration: number, 
    velocity: number,
    destination: AudioNode
  ) => {
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const osc3 = ctx.createOscillator();
    const gain = ctx.createGain();
    
    // Pure sine with harmonics for bell tone
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(freq, time);
    
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2.5, time); // Bell overtone
    
    osc3.type = 'sine';
    osc3.frequency.setValueAtTime(freq * 4, time); // Shimmer
    
    // Quick staccato attack
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(velocity * 0.25, time + 0.008);
    gain.gain.exponentialRampToValueAtTime(velocity * 0.12, time + duration * 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.6);
    
    const osc2Gain = ctx.createGain();
    osc2Gain.gain.setValueAtTime(0.12, time);
    
    const osc3Gain = ctx.createGain();
    osc3Gain.gain.setValueAtTime(0.05, time);
    
    osc1.connect(gain);
    osc2.connect(osc2Gain);
    osc2Gain.connect(gain);
    osc3.connect(osc3Gain);
    osc3Gain.connect(gain);
    gain.connect(destination);
    
    osc1.start(time);
    osc2.start(time);
    osc3.start(time);
    osc1.stop(time + duration);
    osc2.stop(time + duration);
    osc3.stop(time + duration);
  }, []);

  // Pizzicato strings
  const playPizzicato = useCallback((
    ctx: AudioContext,
    freq: number,
    time: number,
    duration: number,
    velocity: number,
    destination: AudioNode
  ) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2500, time);
    filter.frequency.exponentialRampToValueAtTime(600, time + duration * 0.5);
    filter.Q.setValueAtTime(1.5, time);
    
    // Plucky attack
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(velocity * 0.2, time + 0.003);
    gain.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.5);
    
    osc.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    
    osc.start(time);
    osc.stop(time + duration);
  }, []);

  // Warm synth pad
  const playSynthPad = useCallback((
    ctx: AudioContext,
    freq: number,
    time: number,
    duration: number,
    velocity: number,
    destination: AudioNode
  ) => {
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc1.type = 'sawtooth';
    osc1.frequency.setValueAtTime(freq, time);
    
    osc2.type = 'sawtooth';
    osc2.frequency.setValueAtTime(freq * 1.003, time); // Slight detune
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000, time);
    filter.Q.setValueAtTime(0.5, time);
    
    // Soft swell
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(velocity * 0.06, time + 0.15);
    gain.gain.setValueAtTime(velocity * 0.06, time + duration - 0.15);
    gain.gain.linearRampToValueAtTime(0, time + duration);
    
    const merger = ctx.createGain();
    osc1.connect(merger);
    osc2.connect(merger);
    merger.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    
    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + duration + 0.1);
    osc2.stop(time + duration + 0.1);
  }, []);

  // Deep bass
  const playBass = useCallback((
    ctx: AudioContext,
    freq: number,
    time: number,
    duration: number,
    velocity: number,
    destination: AudioNode
  ) => {
    const osc = ctx.createOscillator();
    const subOsc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, time);
    
    subOsc.type = 'sine';
    subOsc.frequency.setValueAtTime(freq / 2, time);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(350, time);
    
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(velocity * 0.3, time + 0.015);
    gain.gain.setValueAtTime(velocity * 0.25, time + duration * 0.5);
    gain.gain.linearRampToValueAtTime(0, time + duration * 0.9);
    
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0.35, time);
    
    osc.connect(gain);
    subOsc.connect(subGain);
    subGain.connect(gain);
    gain.connect(filter);
    filter.connect(destination);
    
    osc.start(time);
    subOsc.start(time);
    osc.stop(time + duration + 0.1);
    subOsc.stop(time + duration + 0.1);
  }, []);

  // Electronic kick drum
  const playKick = useCallback((ctx: AudioContext, time: number, velocity: number, destination: AudioNode) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, time);
    osc.frequency.exponentialRampToValueAtTime(35, time + 0.08);
    
    gain.gain.setValueAtTime(velocity * 0.5, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.25);
    
    osc.connect(gain);
    gain.connect(destination);
    
    osc.start(time);
    osc.stop(time + 0.3);
  }, []);

  // Punchy snare
  const playSnare = useCallback((ctx: AudioContext, time: number, velocity: number, destination: AudioNode) => {
    const bufferSize = ctx.sampleRate * 0.12;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const noiseGain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(2500, time);
    
    noiseGain.gain.setValueAtTime(velocity * 0.18, time);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
    
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(200, time);
    osc.frequency.exponentialRampToValueAtTime(100, time + 0.05);
    oscGain.gain.setValueAtTime(velocity * 0.15, time);
    oscGain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
    
    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(destination);
    
    osc.connect(oscGain);
    oscGain.connect(destination);
    
    noise.start(time);
    osc.start(time);
    osc.stop(time + 0.15);
  }, []);

  // Hi-hat
  const playHiHat = useCallback((ctx: AudioContext, time: number, velocity: number, open: boolean, destination: AudioNode) => {
    const bufferSize = ctx.sampleRate * (open ? 0.15 : 0.04);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    filter.type = 'highpass';
    filter.frequency.setValueAtTime(9000, time);
    
    gain.gain.setValueAtTime(velocity * 0.1, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + (open ? 0.15 : 0.04));
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    
    noise.start(time);
  }, []);

  // Schedule a bar of music
  const scheduleBar = useCallback((
    ctx: AudioContext,
    startTime: number,
    intensity: MusicIntensity,
    dryGain: GainNode,
    wetGain: GainNode
  ) => {
    const tempo = TEMPO_MAP[intensity];
    const beatDuration = 60 / tempo;
    const sixteenthNote = beatDuration / 4;
    
    const melodyPatterns = MELODY_PATTERNS[intensity];
    const melodyPattern = melodyPatterns[patternIndexRef.current % melodyPatterns.length];
    const pizzPatterns = PIZZ_PATTERNS[intensity];
    const pizzChords = pizzPatterns[patternIndexRef.current % pizzPatterns.length];
    const bassPattern = BASS_PATTERNS[intensity];
    
    loopCountRef.current++;
    if (loopCountRef.current >= 2) {
      loopCountRef.current = 0;
      patternIndexRef.current = (patternIndexRef.current + 1) % melodyPatterns.length;
    }
    
    const velocityMultiplier = intensity === 'calm' ? 0.65 : 
                               intensity === 'waiting' ? 0.75 :
                               intensity === 'medium' ? 0.85 :
                               intensity === 'intense' ? 0.95 :
                               intensity === 'critical' ? 1.0 : 0.8;

    // Glockenspiel melody on sixteenth notes
    melodyPattern.forEach((note, i) => {
      const time = startTime + (i * sixteenthNote);
      const freq = NOTE_FREQUENCIES[note];
      if (freq) {
        playGlockenspiel(ctx, freq, time, sixteenthNote * 0.5, velocityMultiplier, wetGain);
      }
    });

    // Pizzicato strings on each beat
    for (let beat = 0; beat < 4; beat++) {
      const chord = pizzChords[beat % pizzChords.length];
      chord.forEach((note, noteIndex) => {
        const time = startTime + (beat * beatDuration) + (noteIndex * sixteenthNote * 0.4);
        const freq = NOTE_FREQUENCIES[note];
        if (freq) {
          playPizzicato(ctx, freq, time, beatDuration * 0.35, velocityMultiplier * 0.7, wetGain);
        }
      });
    }

    // Synth pad every 2 beats
    if (intensity !== 'defeat') {
      for (let i = 0; i < 2; i++) {
        const time = startTime + (i * beatDuration * 2);
        const padNote = i === 0 ? 'C4' : 'G4';
        const freq = NOTE_FREQUENCIES[padNote];
        if (freq) {
          playSynthPad(ctx, freq, time, beatDuration * 2, velocityMultiplier * 0.4, wetGain);
        }
      }
    }

    // Bass on quarter notes
    bassPattern.slice(0, 4).forEach((note, i) => {
      const time = startTime + (i * beatDuration);
      const freq = NOTE_FREQUENCIES[note];
      if (freq) {
        playBass(ctx, freq, time, beatDuration * 0.75, velocityMultiplier, dryGain);
      }
    });

    // Drums
    for (let beat = 0; beat < 4; beat++) {
      const beatTime = startTime + (beat * beatDuration);
      
      // Kick on 1 and 3
      if (beat === 0 || beat === 2) {
        playKick(ctx, beatTime, velocityMultiplier, dryGain);
      }
      
      // Snare on 2 and 4
      if (beat === 1 || beat === 3) {
        playSnare(ctx, beatTime, velocityMultiplier * 0.7, dryGain);
      }
      
      // Hi-hats on eighth notes
      playHiHat(ctx, beatTime, velocityMultiplier * 0.5, false, dryGain);
      playHiHat(ctx, beatTime + beatDuration / 2, velocityMultiplier * 0.35, false, dryGain);
      
      // Open hi-hat on beat 4 offbeat
      if (beat === 3 && intensity !== 'calm') {
        playHiHat(ctx, beatTime + beatDuration * 0.75, velocityMultiplier * 0.4, true, dryGain);
      }
    }

    return beatDuration * 4;
  }, [playGlockenspiel, playPizzicato, playSynthPad, playBass, playKick, playSnare, playHiHat]);

  // Main music loop
  const startMusicLoop = useCallback(() => {
    if (!audioContextRef.current || !masterGainRef.current || !reverbRef.current) return;
    
    const ctx = audioContextRef.current;
    let nextBarTime = ctx.currentTime + 0.1;
    
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    dryGain.gain.setValueAtTime(0.75, ctx.currentTime);
    wetGain.gain.setValueAtTime(0.4, ctx.currentTime);
    
    dryGain.connect(compressorRef.current!);
    wetGain.connect(reverbRef.current);
    reverbRef.current.connect(compressorRef.current!);

    const scheduleAhead = () => {
      if (!isPlayingRef.current || !audioContextRef.current) return;
      
      const currentTime = audioContextRef.current.currentTime;
      
      while (nextBarTime < currentTime + 0.5) {
        const barDuration = scheduleBar(
          audioContextRef.current,
          nextBarTime,
          intensityRef.current,
          dryGain,
          wetGain
        );
        nextBarTime += barDuration;
      }
      
      animationFrameRef.current = requestAnimationFrame(scheduleAhead);
    };
    
    scheduleAhead();
  }, [scheduleBar]);

  // Initialize audio
  const initAudio = useCallback(async () => {
    if (audioContextRef.current) return;
    
    try {
      const ctx = new (window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      audioContextRef.current = ctx;
      
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(isMuted ? 0 : volume, ctx.currentTime);
      masterGainRef.current = masterGain;
      
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-18, ctx.currentTime);
      compressor.knee.setValueAtTime(12, ctx.currentTime);
      compressor.ratio.setValueAtTime(4, ctx.currentTime);
      compressor.attack.setValueAtTime(0.003, ctx.currentTime);
      compressor.release.setValueAtTime(0.2, ctx.currentTime);
      compressorRef.current = compressor;
      
      reverbRef.current = createReverb(ctx);
      
      compressor.connect(masterGain);
      masterGain.connect(ctx.destination);
      
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      
      isPlayingRef.current = true;
      setIsPlaying(true);
      startMusicLoop();
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  }, [isMuted, volume, createReverb, startMusicLoop]);

  // Removed duplicate user interaction handlers - now handled by Index.tsx start() call

  // Cleanup
  useEffect(() => {
    return () => {
      isPlayingRef.current = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!audioContextRef.current && !isMuted) {
      initAudio();
    }
  }, [initAudio, isMuted]);

  const stop = useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  }, []);

  const setIntensity = useCallback((intensity: MusicIntensity) => {
    intensityRef.current = intensity;
  }, []);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    localStorage.setItem('67chaos-music-volume', newVolume.toString());
    if (masterGainRef.current && audioContextRef.current) {
      masterGainRef.current.gain.linearRampToValueAtTime(
        isMuted ? 0 : newVolume,
        audioContextRef.current.currentTime + 0.1
      );
    }
  }, [isMuted]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      localStorage.setItem('67chaos-music-muted', newMuted.toString());
      if (masterGainRef.current && audioContextRef.current) {
        masterGainRef.current.gain.linearRampToValueAtTime(
          newMuted ? 0 : volume,
          audioContextRef.current.currentTime + 0.1
        );
      }
      if (!newMuted && !audioContextRef.current) {
        initAudio();
      }
      return newMuted;
    });
  }, [volume, initAudio]);

  return {
    start,
    stop,
    setIntensity,
    setVolume,
    toggleMute,
    isMuted,
    volume,
    isPlaying,
  };
};
